import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

// Store
import { RootState, Dispatch } from '../store/store';

// Components
import Header from '../components/Header/index';
import Sidebar from '../components/Sidebar/index';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import ProtectedRoute from '../components/ProtectedRoute/ProtectedRoute';
import { AbilityContext } from '../components/Can/Can';
import defineAbility from '../ability/defineAbility';
import ROUTE from '../constants/route-constants';
import BreadCrumb from '../components/Breadcrumb/Breadcrumb';
import { ROLES } from '../constants/roles';

type DefaultLayoutProps = ReturnType<typeof mapState> &
  ReturnType<typeof mapDispatch>;

const DefaultLayout: React.FC<DefaultLayoutProps> = ({
  currentUser,
  isAuthenticated,
  getCurrentUser,
  logOutUser,
  isUserLoaded,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const isUserAvailable = Boolean(currentUser?.id);

  const ability = defineAbility(currentUser);

  const isViewBreadcrumbs = (currentUser?.memberData && currentUser?.memberData?.role !== ROLES.CUSTOMER || location.pathname !== ROUTE.HOME);

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const fetchUserData = async () => {
    try {
      const data = await getCurrentUser();

      if (!data?.memberData?.active) {
        navigate(ROUTE.PROFILE);
      }
    } catch {
      logOutUser();
      navigate(ROUTE.AUTH_SIGN_IN, { replace: true });
    }
  };

  useEffect(() => {
    if (isAuthenticated && !currentUser?.id) {
      fetchUserData();
    }
  }, []);

  return (
    <div className="c-default-layout dark:bg-boxdark-2 dark:text-bodydark">
      <div className="c-default-layout__wrapper flex h-screen overflow-hidden">
        <ProtectedRoute
          isAuthenticated={isAuthenticated}
          isUserAvailable={isUserAvailable}
        >
          <AbilityContext.Provider value={ability}>
            <Sidebar
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              logo={currentUser?.organization?.logo}
              isUserLoaded={isUserLoaded}
              isCustomer={currentUser?.memberData && currentUser?.memberData?.role === ROLES.CUSTOMER}
            />
            <div className="c-default-layout__content relative flex flex-1 flex-col overflow-hidden">
              <Header
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
              />
              <main className="c-default-layout__main min-h-app-content overflow-y-auto overflow-x-hidden px-4 pt-4 md:px-6 md:pt-6 2xl:px-9 2xl:pt-9 flex flex-col">
                {isViewBreadcrumbs && <BreadCrumb />}
                <Outlet />
              </main>
            </div>
            <div id="portal" />
          </AbilityContext.Provider>
        </ProtectedRoute>
      </div>
    </div>
  );
};

const mapState = (state: RootState) => ({
  isAuthenticated: state.auth?.isAuthenticated,
  currentUser: state.user.profile,
  isUserLoaded: state.user.isUserLoaded,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getCurrentUser: dispatch.user.getCurrentUser,
  logOutUser: dispatch.auth.logOutUser,
});

export default connect(mapState, mapDispatch)(DefaultLayout);
